import { Plus, Ruler, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  isValidPanel,
  panelArea,
  parseDimension,
  totalArea,
} from '@obraplan/core/domain/planning/area-measurement';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { cn } from '@/lib/cn';

/**
 * A produção pela medida (RN-379).
 *
 * Quem lança alvenaria, chapisco, emboço, reboco, gesso ou drywall no RDO não sabe os
 * metros quadrados: sabe que levantou um pano de 2,80 por 5,00. Este modal pergunta a
 * altura e a largura de cada pano, mostra a área de cada um e a soma, e devolve o total
 * para o campo de quantidade — que continua editável, porque a medida é ajuda, e não
 * obrigação.
 *
 * O modal abre em **todo serviço medido em m²**, e não numa lista de nomes: a lista
 * envelhece no primeiro serviço novo do catálogo, e a unidade não. Piso e forro também são
 * m² — para eles a "altura" é o comprimento, e o rodapé diz isso.
 *
 * Componente puro: recebe a unidade, devolve o número. Quem põe o número no campo é quem
 * abre o modal.
 */
export interface AreaMeasurementDialogProps {
  open: boolean;
  /** O que está sendo medido — "ASSENTAMENTO DE ALVENARIA · 3º Pavimento". */
  subject?: string | undefined;
  /** A sigla da unidade, para o total: "m²". */
  unitName: string;
  /** As casas da unidade: o total é arredondado por elas (RN-020). */
  decimals: number;
  onOpenChange: (open: boolean) => void;
  /** A área somada, já na precisão da unidade — vai para o campo de quantidade. */
  onApply: (total: number) => void;
}

interface Pano {
  readonly id: number;
  readonly altura: string;
  readonly largura: string;
}

const PANO_VAZIO = (id: number): Pano => ({ id, altura: '', largura: '' });

function formatArea(valor: number, decimals: number): string {
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function AreaMeasurementDialog({
  open,
  subject,
  unitName,
  decimals,
  onOpenChange,
  onApply,
}: AreaMeasurementDialogProps) {
  const [panos, setPanos] = useState<readonly Pano[]>([PANO_VAZIO(1)]);
  const [proximoId, setProximoId] = useState(2);

  // Abre sempre limpo, com um pano: a medida de ontem não é a de hoje.
  useEffect(() => {
    if (!open) return;
    setPanos([PANO_VAZIO(1)]);
    setProximoId(2);
  }, [open]);

  const medidos = panos.map((pano) => {
    const height = parseDimension(pano.altura) ?? Number.NaN;
    const width = parseDimension(pano.largura) ?? Number.NaN;
    const panel = { height, width };
    return { pano, panel, valido: isValidPanel(panel) };
  });
  const total = totalArea(
    medidos.map((item) => item.panel),
    decimals,
  );
  const temPano = medidos.some((item) => item.valido);

  const alterar = (id: number, campo: 'altura' | 'largura', valor: string): void => {
    setPanos((atuais) =>
      atuais.map((pano) => (pano.id === id ? { ...pano, [campo]: valor } : pano)),
    );
  };

  const adicionar = (): void => {
    setPanos((atuais) => [...atuais, PANO_VAZIO(proximoId)]);
    setProximoId((id) => id + 1);
  };

  const remover = (id: number): void => {
    setPanos((atuais) => (atuais.length === 1 ? atuais : atuais.filter((p) => p.id !== id)));
  };

  const aplicar = (): void => {
    if (!temPano) return;
    onApply(total);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ruler className="size-5" aria-hidden="true" />
            Produção pela medida
          </DialogTitle>
          <DialogDescription>
            {subject === undefined ? '' : `${subject} — `}a altura e a largura de cada pano, em
            metros. A área é somada e vai para a quantidade.
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
          {medidos.map(({ pano, panel, valido }, indice) => {
            const n = indice + 1;
            return (
              <div
                key={pano.id}
                className="grid grid-cols-[1fr_1fr_auto] items-end gap-2 sm:grid-cols-[1fr_1fr_6rem_auto]"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`pano-${String(pano.id)}-altura`}>
                    {indice === 0 ? 'Altura (m)' : <span className="sr-only">Altura (m)</span>}
                  </Label>
                  <Input
                    id={`pano-${String(pano.id)}-altura`}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    placeholder="2,80"
                    aria-label={`Altura do pano ${String(n)} (m)`}
                    autoFocus={indice === 0}
                    value={pano.altura}
                    onChange={(event) => alterar(pano.id, 'altura', event.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`pano-${String(pano.id)}-largura`}>
                    {indice === 0 ? 'Largura (m)' : <span className="sr-only">Largura (m)</span>}
                  </Label>
                  <Input
                    id={`pano-${String(pano.id)}-largura`}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    placeholder="5,00"
                    aria-label={`Largura do pano ${String(n)} (m)`}
                    value={pano.largura}
                    onChange={(event) => alterar(pano.id, 'largura', event.target.value)}
                    onKeyDown={(event) => {
                      // Enter na última largura abre o próximo pano: a trena não para.
                      if (event.key === 'Enter' && indice === medidos.length - 1 && valido) {
                        event.preventDefault();
                        adicionar();
                      }
                    }}
                  />
                </div>
                {/*
                 * A área do pano: no monitor é a coluna ao lado da largura; no celular não
                 * cabe na linha e desce para baixo dos campos — e aí ganha o nome do pano,
                 * senão é um número solto entre duas linhas de campos.
                 */}
                <p
                  className={cn(
                    'col-span-3 text-xs tabular-nums sm:col-span-1 sm:h-11 sm:text-right sm:text-sm sm:leading-11 md:h-9 md:leading-9',
                    valido ? 'text-(--color-content)' : 'text-(--color-content-muted)',
                  )}
                  aria-label={`Área do pano ${String(n)}`}
                >
                  <span className="text-(--color-content-muted) sm:hidden">
                    Pano {String(n)} ·{' '}
                  </span>
                  {valido ? (
                    `${formatArea(panelArea(panel), decimals)} ${unitName}`
                  ) : (
                    <>
                      <span className="sm:hidden">sem medida</span>
                      <span className="hidden sm:inline">—</span>
                    </>
                  )}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="col-start-3 row-start-1 sm:col-start-4"
                  disabled={panos.length === 1}
                  aria-label={`Remover pano ${String(n)}`}
                  onClick={() => remover(pano.id)}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </div>
            );
          })}

          <div>
            <Button type="button" variant="secondary" size="sm" onClick={adicionar}>
              <Plus aria-hidden="true" />
              Adicionar pano
            </Button>
          </div>

          <p className="text-xs text-(--color-content-muted)">
            Para piso ou forro, a altura é o comprimento. Vão de porta e janela não é descontado
            aqui: lance o pano cheio ou ajuste a quantidade depois.
          </p>
        </div>

        {/*
         * O total fica acima dos botões, e não no rodapé: no celular o rodapé empilha de
         * baixo para cima, e o total — que é o que se confere antes de usar — ia parar
         * embaixo do "Cancelar".
         */}
        <p
          className="mt-4 flex items-baseline justify-between gap-3 border-t border-(--color-border-subtle) pt-3 text-sm text-(--color-content-muted)"
          aria-live="polite"
        >
          {'Total: '}
          <strong className="text-base font-semibold tabular-nums text-(--color-content)">
            {formatArea(total, decimals)} {unitName}
          </strong>
        </p>

        <DialogFooter className="mt-3">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button variant="primary" className="min-h-11" disabled={!temPano} onClick={aplicar}>
            Usar {formatArea(total, decimals)} {unitName}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * O botão ao lado do campo de quantidade que abre o modal.
 *
 * Mora aqui, e não em cada tela, porque são três campos de quantidade — o lançamento do
 * RDO, a checklist que abre dele e a medição da conferência — e o gesto é o mesmo nos três.
 * Leva ícone e texto em qualquer tela: nos três lugares o campo tem a linha inteira no
 * celular, e um ícone sozinho não diz a quem nunca o viu que ali se calcula pela medida.
 */
export function AreaMeasurementButton({
  subject,
  unitName,
  decimals,
  disabled = false,
  onApply,
}: {
  subject?: string | undefined;
  unitName: string;
  decimals: number;
  disabled?: boolean;
  onApply: (total: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const nome = subject === undefined ? 'Calcular pela medida' : `Calcular pela medida: ${subject}`;
  return (
    <>
      <Button
        type="button"
        variant="secondary"
        className="shrink-0"
        disabled={disabled}
        aria-label={nome}
        title="Altura × largura de cada pano"
        onClick={() => setOpen(true)}
      >
        <Ruler aria-hidden="true" />
        Pela medida
      </Button>
      <AreaMeasurementDialog
        open={open}
        subject={subject}
        unitName={unitName}
        decimals={decimals}
        onOpenChange={setOpen}
        onApply={onApply}
      />
    </>
  );
}
